import { Reveal, SectionHeading } from "./Reveal";
import { Globe, Mic, Check, ArrowUpRight } from "lucide-react";
import { scrollToId } from "../lib/site";

const SERVICES = [
  {
    id: "website",
    icon: Globe,
    accent: "cyan",
    kicker: "Product 01",
    title: "AI Website Maker",
    tagline: "From Google Business link to live site in 48 hours.",
    image:
      "https://images.unsplash.com/photo-1567880905822-56f8e06fe630?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwYXJ0aXNhbiUyMGNvZmZlZSUyMHNob3AlMjBsb2NhbCUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nfGVufDB8fHx8MTc4OTc5MTg3OXww&ixlib=rb-4.1.0&q=85",
    features: [
      "Auto-imports reviews, photos, hours & services from Google",
      "Conversion-first design tuned for your industry",
      "Free live preview before you pay anything",
      "Booking forms, click-to-call & maps built in",
      "Hosting, SSL & updates fully managed",
    ],
    cta: "Get my free preview",
    target: "preview",
    testId: "services-website",
  },
  {
    id: "voice",
    icon: Mic,
    accent: "violet",
    kicker: "Product 02",
    title: "AI Voice Agent",
    tagline: "A 24/7 receptionist that sounds human and never sleeps.",
    image:
      "https://images.pexels.com/photos/7800666/pexels-photo-7800666.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      "Answers every call on the first ring — day & night",
      "Books appointments straight into your calendar",
      "Handles FAQs, pricing questions & directions",
      "Triages emergencies and dispatches your on-call tech",
      "Sends instant SMS confirmations to callers",
    ],
    cta: "Build my voice agent",
    target: "voice-inquiry",
    testId: "services-voice",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-36 px-4 sm:px-6 lg:px-8 bg-[#0A0D14]/50">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          testId="services-heading"
          kicker="What we build"
          title="Two products. One growth engine."
          sub="Deploy them together or start with either — both are trained on your real business data."
        />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.14}>
              <article
                data-testid={`${s.testId}-card`}
                className="glow-card group relative rounded-2xl overflow-hidden h-full flex flex-col"
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D111A] via-[#0D111A]/40 to-transparent" />
                  <div className="absolute bottom-4 left-6 flex items-center gap-3">
                    <span
                      className={`flex h-11 w-11 items-center justify-center rounded-xl backdrop-blur ring-1 ${
                        s.accent === "cyan"
                          ? "bg-cyan-400/15 ring-cyan-400/40"
                          : "bg-violet-500/15 ring-violet-400/40"
                      }`}
                    >
                      <s.icon className={`h-5 w-5 ${s.accent === "cyan" ? "text-cyan-300" : "text-violet-300"}`} />
                    </span>
                    <span className="font-mono text-xs font-semibold tracking-[0.25em] uppercase text-slate-300">
                      {s.kicker}
                    </span>
                  </div>
                </div>
                <div className="p-7 sm:p-9 flex flex-col flex-1">
                  <h3 className="font-display text-2xl font-semibold tracking-tight text-slate-50">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400">{s.tagline}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                        <Check
                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                            s.accent === "cyan" ? "text-cyan-400" : "text-violet-400"
                          }`}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`${s.testId}-cta`}
                    onClick={() => scrollToId(s.target)}
                    className={`mt-8 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-[background-color,box-shadow] duration-300 ${
                      s.accent === "cyan"
                        ? "bg-cyan-400 text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_32px_rgba(0,240,255,0.4)]"
                        : "bg-violet-500 text-white hover:bg-violet-400 hover:shadow-[0_0_32px_rgba(112,0,255,0.5)]"
                    }`}
                  >
                    {s.cta} <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
